import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  CustomRoutes,
  localStorageStore,
  Resource,
  type AuthProvider,
  type CoreAdminProps,
} from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile.ts";
import { boms } from "../bom";
import companies from "../companies";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import contacts from "../contacts";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import { design_requests } from "../design-requests";
import { ForcePasswordChangePage } from "../login/ForcePasswordChangePage.tsx";
import {
  delivery_challans,
  invoices,
  proforma_invoices,
  purchase_orders,
  quotations,
  sales_orders,
} from "../documents";
import { enquiries } from "../enquiries";
import { goods_receipts, grn_items } from "../grn";
import { hr } from "../hr";
import { inventory_items } from "../inventory";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { StartPage } from "../login/StartPage.tsx";
import { ImportPage } from "../misc/ImportPage";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { opportunities } from "../opportunities";
import { projects } from "../projects";
import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/supabase";
import type { CrmDataProvider } from "../providers/types";
import { PermissionsPage, RBACRoute } from "../rbac";
import { rfqs } from "../rfqs";
import sales, {
  UserRolesPage,
  UsersListPage,
  AutoInvoicingPage,
  CustomerAgingPage,
  CreditLimitsPage,
  PDCTrackingPage,
  CreditNotesPage,
  ReceiptMatchingPage
} from "../sales";
import { service_tickets } from "../service-tickets";
import { hsn_codes } from "../settings/hsn-codes";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import { tax_slabs } from "../settings/tax-slabs";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { terms_templates } from "../terms_templates";
import { vendor_bills } from "../vendor_bills";
import { vendors } from "../vendors";
import { VendorOnboardingPage } from "../vendors/VendorOnboardingPage";
import { work_orders } from "../work-orders";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import {
  defaultCompanySectors,
  defaultDarkModeLogo,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider } from "./i18nProvider";

const defaultStore = localStorageStore(undefined, "CRM");

const sharedQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      // X-2: was "offlineFirst", which served persisted/cached data and
      // de-prioritised the network — so writes didn't show until a hard
      // refresh. "online" (the default) keeps the persisted cache for instant
      // first paint but always refetches (staleTime:0 + refetchOnMount), and
      // lets post-mutation invalidations actually hit the network. Trade-off:
      // no offline *data* access (PWA asset precaching is unaffected).
      networkMode: "online",
    },
    mutations: {
      // X-2: mutations must require the network so they can't be silently
      // queued/treated as offline; this also guarantees the follow-up
      // invalidation refetch runs against the server.
      networkMode: "online",
    },
  },
});

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/gsw-erp/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
  store = defaultStore,
  googleWorkplaceDomain = import.meta.env.VITE_GOOGLE_WORKPLACE_DOMAIN,
  disableEmailPasswordAuthentication = import.meta.env
    .VITE_DISABLE_EMAIL_PASSWORD_AUTHENTICATION === "true",
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    new Image();
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  if (!store.getItem(CONFIGURATION_STORE_KEY)) {
    store.setItem(CONFIGURATION_STORE_KEY, {
      companySectors,
      noteStatuses,
      taskTypes,
      title,
      darkModeLogo,
      lightModeLogo,
      googleWorkplaceDomain,
      disableEmailPasswordAuthentication,
    } satisfies ConfigurationContextValue);
  }

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (props: CoreAdminProps) => {
  return (
    <Admin queryClient={sharedQueryClient} layout={Layout} dashboard={Dashboard} {...props}>
      <CustomRoutes noLayout>
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/oauth-consent" element={<OAuthConsentPage />} />
        <Route path="/onboard" element={<VendorOnboardingPage />} />
        <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
      </CustomRoutes>
      <CustomRoutes noLayout>
        <Route path="/import" element={<ImportPage />} />
      </CustomRoutes>
      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={UserRolesPage.path} element={<RBACRoute permission="manage_users"><UserRolesPage /></RBACRoute>} />
        <Route path={UsersListPage.path} element={<RBACRoute permission="manage_users"><UsersListPage /></RBACRoute>} />
        <Route
          path="/vendors/approval-queue"
          element={<vendors.VendorApprovalQueue />}
        />
        <Route
          path="/credit-limits"
          element={
            <RBACRoute permission="view_receivables">
              <CreditLimitsPage />
            </RBACRoute>
          }
        />
      </CustomRoutes>
      <Resource name="enquiries" {...enquiries} />
      <Resource name="opportunities" {...opportunities} />
      <Resource name="projects" {...projects} />
      <Resource name="design_requests" {...design_requests} />
      <Resource name="boms" {...boms} />
      <Resource name="rfqs" {...rfqs} />
      <Resource name="quotations" {...quotations} />
      <Resource name="sales_orders" {...sales_orders} />
      <Resource name="proforma_invoices" {...proforma_invoices} />
      <Resource name="purchase_orders" {...purchase_orders} />
      <Resource
        name="goods_receipts"
        {...goods_receipts}
        options={{ label: "Goods Receipts" }}
      />
      <Resource name="grn_items" {...grn_items} />
      <Resource name="customer_invoices" {...invoices} />
      <Resource name="delivery_challans" {...delivery_challans} />
      <Resource
        name="inventory_items"
        {...inventory_items}
        options={{ label: "Inventory" }}
      />
      <Resource name="inventory_transactions" />
      <Resource name="project_vendors" />
      <Resource name="cost_entries" />
      <Resource name="product_categories" recordRepresentation="name" />
      <Resource name="vendors" {...vendors} />
      <Resource
        name="vendor_bills"
        {...vendor_bills}
        options={{ label: "Vendor Bills" }}
      />
      <Resource
        name="work_orders"
        {...work_orders}
        options={{ label: "Work Orders" }}
      />
      <Resource
        name="service_tickets"
        {...service_tickets}
        options={{ label: "Service Tickets" }}
      />
      <Resource
        name="employees"
        {...hr.employees}
        options={{ label: "Employees" }}
      />
      <Resource
        name="hr_departments"
        {...hr.departments}
        options={{ label: "Departments" }}
      />
      <Resource
        name="hr_designations"
        {...hr.designations}
        options={{ label: "Designations" }}
      />
      <Resource
        name="hr_holidays"
        {...hr.holidays}
        options={{ label: "Holidays" }}
      />
      <Resource
        name="hr_leave_types"
        {...hr.leaveTypes}
        options={{ label: "Leave Types" }}
      />
      <Resource
        name="hr_leave_requests"
        {...hr.leaveRequests}
        options={{ label: "Leave Requests" }}
      />
      <Resource
        name="hr_attendance"
        {...hr.attendance}
        options={{ label: "Attendance" }}
      />
      <Resource
        name="hr_payroll_components"
        {...hr.payrollComponents}
        options={{ label: "Payroll Components" }}
      />
      <Resource
        name="hr_salary_structures"
        {...hr.salaryStructures}
        options={{ label: "Salary Structures" }}
      />
      <Resource
        name="hr_payroll_runs"
        {...hr.payrollRuns}
        options={{ label: "Payroll" }}
      />
      <Resource
        name="hr_payroll_slips"
        {...hr.payrollSlips}
        options={{ label: "Payslips" }}
      />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="tasks" />
      <Resource name="sales" {...sales} />
      <Resource
        name="dispatch_to_invoice_queue"
        list={AutoInvoicingPage}
        options={{ label: "Auto Invoices" }}
      />
      <Resource
        name="customer_aging_view"
        list={CustomerAgingPage}
        options={{ label: "Customer Aging" }}
      />
      <Resource
        name="pdc_cheques"
        list={PDCTrackingPage}
        options={{ label: "PDC Tracking" }}
      />
      <Resource
        name="credit_notes"
        list={CreditNotesPage}
        options={{ label: "Credit Notes" }}
      />
      <Resource
        name="payment_receipts"
        list={ReceiptMatchingPage}
        options={{ label: "Receipt Matching" }}
      />
      <Resource name="receipt_allocations" />
      <Resource name="email_outbox" />
      <Resource name="tags" />
      <Resource name="terms_templates" {...terms_templates} />
      <Resource name="tax_slabs" {...tax_slabs} />
      <Resource name="hsn_codes" {...hsn_codes} />
      <Resource name="uqc_codes" />
      {/* UQC reference table — no UI, used in HSN forms */}
      {/* RBAC resources — no UI, used by ReferenceInput and UserRolesPage */}
      <Resource name="roles" />
      <Resource name="permissions" {...PermissionsPage} />
      <Resource name="role_permissions" />
    </Admin>
  );
};

const MobileAdmin = (props: CoreAdminProps) => {
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={sharedQueryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={sharedQueryClient}
        layout={MobileLayout}
        dashboard={MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
          <Route path="/onboard" element={<VendorOnboardingPage />} />
          <Route path={ForcePasswordChangePage.path} element={<ForcePasswordChangePage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        <Resource name="tasks" list={MobileTasksList} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
